import React, { useState } from 'react';
import Tippy from '@tippy.js/react';
import { ApButtonLink } from '@alixpartners/ui-components';

import { SortArrow } from './SortArrow';
import { getTotal, sortByProperty } from '../../../../utils/common';
import { generateCostsDetails } from '../../../../utils/mailTo';
import { IExpenseDetail } from '../../../../types';

import 'tippy.js/themes/light.css';
import './EnvironmentSection.scss';
import {
  StyledApModal,
  InputModalContent,
  Table,
  HeaderWrapper,
  SubTitle,
  ServiceListContainer,
  InfoLink,
  InputModalFooter,
  TableHeaderBlock,
  TableDataBlock,
  TableData,
  TableDataRow,
  TotalDataRow,
  TableHeaderRow,
} from './ExpenseDetailsModal.styles';

interface IExpenseDetailsModalProps {
  data: {
    projectCodeName?: string;
    userName?: string;
    expenseDetails: IExpenseDetail[];
  };
  isOpen?: boolean;
  hasClosed: () => any;
}

export const COLUMN_NAMES = {
  service: 'service',
  resource: 'resource',
  lastMonth: 'last-month',
  thisMonth: 'this-month',
  delta: 'delta',
} as const;
type Keys = keyof typeof COLUMN_NAMES;
export type ColumnNamesValues = typeof COLUMN_NAMES[Keys];
export const ASC = 'asc';
export const DESC = 'desc';

export const ExpenseDetailsModal: React.FC<IExpenseDetailsModalProps> = ({
  data: { projectCodeName, userName, expenseDetails },
  isOpen,
  hasClosed,
}) => {
  const [sortingOrder, setSortingOrder] = useState(ASC);
  const [sortingHeader, setSortingHeader] = useState<ColumnNamesValues>(
    COLUMN_NAMES.thisMonth
  );

  const renderHeaderBlock = (
    headerTitle: string,
    headerName: ColumnNamesValues
  ) => {
    const isSorted = sortingHeader === headerName;
    return (
      <TableHeaderBlock
        sortingOrder={isSorted && sortingOrder}
        headerName={headerName}
        onClick={() => {
          handleHeaderClick(headerName);
        }}
      >
        <div>{headerTitle}</div>
        <div>{isSorted && <SortArrow />}</div>
      </TableHeaderBlock>
    );
  };

  const handleHeaderClick = (headerName: ColumnNamesValues) => {
    if (sortingHeader === headerName) {
      setSortingOrder(sortingOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortingHeader(headerName);
      setSortingOrder('asc');
    }
  };

  const renderDataBlock = (
    headerName: ColumnNamesValues,
    data: typeof detailedExpenses[0]
  ) => {
    if (
      headerName === COLUMN_NAMES.service ||
      headerName === COLUMN_NAMES.resource
    ) {
      return (
        <TableDataBlock headerName={headerName}>
          <strong>{data[COLUMN_NAMES.service]}</strong>
          <div>{data[COLUMN_NAMES.resource]}</div>
        </TableDataBlock>
      );
    }

    return (
      <TableDataBlock headerName={headerName}>
        <div>
          ${' '}
          {data[headerName] && typeof data[headerName] === 'number'
            ? data[headerName].toFixed()
            : data[headerName]}
        </div>
      </TableDataBlock>
    );
  };

  const serviceList = [
    'Azure Devops',
    'Power BI Workspaces',
    'GlobalScape',
    'Tableau Server costs',
    'Tableau License costs',
    'Confluence licenses for Help',
    'All other ETS re-charge costs',
  ];

  const detailedExpenses = expenseDetails.map((data: IExpenseDetail) => {
    const delta = data.currentMonthExpenses - data.previousMonthExpenses;

    return {
      [COLUMN_NAMES.service]: data.serviceName,
      [COLUMN_NAMES.resource]: data.resourceId,
      [COLUMN_NAMES.lastMonth]: data.previousMonthExpenses,
      [COLUMN_NAMES.thisMonth]: data.currentMonthExpenses,
      [COLUMN_NAMES.delta]: delta.toFixed() === '-0' ? 0 : +delta.toFixed(),
    };
  });

  const detailedExpensesSorted = detailedExpenses.sort(
    sortByProperty(sortingHeader, sortingOrder)
  );

  const detailedExpensesTotal: typeof detailedExpensesSorted[0] | null =
    getTotal(detailedExpensesSorted);

  return (
    <StyledApModal
      isDismissible
      isOpen={isOpen}
      hasClosed={hasClosed}
      header={
        <HeaderWrapper>
          <div>Cost details</div>
          <SubTitle>
            The table below shows your Azure costs only. The{' '}
            <Tippy
              arrow={true}
              placement="bottom"
              animation="fade"
              boundary="window"
              interactive={true}
              theme="light"
              trigger="click"
              content={
                <ServiceListContainer>
                  <ul>
                    {serviceList.map((service, index) => (
                      <li key={index}>{service}</li>
                    ))}
                  </ul>
                </ServiceListContainer>
              }
            >
              <InfoLink>following items</InfoLink>
            </Tippy>{' '}
            will be charged in addition in your ETS re-charge.
          </SubTitle>
        </HeaderWrapper>
      }
      footer={() => (
        <InputModalFooter>
          <ApButtonLink
            href={generateCostsDetails(userName || '', projectCodeName || '')}
            target="_blank"
            rel="noopener noreferrer"
          >
            Questions about these costs?
          </ApButtonLink>
        </InputModalFooter>
      )}
    >
      <InputModalContent>
        <Table>
          <TableHeaderRow>
            {renderHeaderBlock('Azure Service Name', COLUMN_NAMES.service)}
            {renderHeaderBlock('Last month actual', COLUMN_NAMES.lastMonth)}
            {renderHeaderBlock('This month actual', COLUMN_NAMES.thisMonth)}
            {renderHeaderBlock('Δ (per/m)', COLUMN_NAMES.delta)}
          </TableHeaderRow>
          <TableData>
            {detailedExpensesSorted.map(
              (data: typeof detailedExpensesSorted[0], index: number) => (
                <TableDataRow key={index}>
                  {renderDataBlock(COLUMN_NAMES.service, data)}
                  {renderDataBlock(COLUMN_NAMES.lastMonth, data)}
                  {renderDataBlock(COLUMN_NAMES.thisMonth, data)}
                  {renderDataBlock(COLUMN_NAMES.delta, data)}
                </TableDataRow>
              )
            )}
          </TableData>
          {detailedExpensesTotal && (
            <TotalDataRow>
              <TableDataBlock headerName={COLUMN_NAMES.service}>
                <strong>Total:</strong>
              </TableDataBlock>
              {renderDataBlock(COLUMN_NAMES.lastMonth, detailedExpensesTotal)}
              {renderDataBlock(COLUMN_NAMES.thisMonth, detailedExpensesTotal)}
              {renderDataBlock(COLUMN_NAMES.delta, detailedExpensesTotal)}
            </TotalDataRow>
          )}
        </Table>
      </InputModalContent>
    </StyledApModal>
  );
};
